'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth';

interface AuthGuardProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireAuth?: boolean; // true = require auth to access, false = require NOT auth to access
}

export function AuthGuard({ children, redirectTo = '/login', requireAuth = true }: AuthGuardProps) {
  const router = useRouter();
  const { user, isInitializing } = useAuth();

  useEffect(() => {
    if (isInitializing) return; // Wait for AuthProvider.checkAuth() to finish

    const isAuthenticated = !!user;

    if (requireAuth && !isAuthenticated) {
      router.push(redirectTo);
    } else if (!requireAuth && isAuthenticated) {
      router.push('/dashboard');
    }
  }, [isInitializing, user, requireAuth, redirectTo, router]);

  // Show spinner while the initial auth check is running
  if (isInitializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (requireAuth && !user && !isInitializing) {
    return null; // Redirect in progress
  }

  if (!requireAuth && user && !isInitializing) {
    return null; // Redirect in progress
  }

  return <>{children}</>;
}
